import { apiClient } from '../lib/api';

// Default feature flags (fallback if API fails)
const defaultFeatureFlags = {
  createWorkflow: false,
  chatPanel: false,
  monitors: false,
  icpDashboard: true,  // ICP Dashboard / Signals page
  campaignScheduling: false,  // Campaign scheduling UI - disabled until backend crons are ready
  clientPortal: import.meta.env.DEV,  // Hidden client portal (/portal) - local dev on; deployed envs need the DB flag
  portalEvents: import.meta.env.DEV,  // Portal Events tab - local dev on; deployed envs need the DB flag
} as const;

// Runtime feature flags loaded from API
let runtimeFeatureFlags: Record<string, boolean> = { ...defaultFeatureFlags };

// Resolves once the first runtime load finishes (success OR failure), so route
// gates can wait instead of deciding on compile-time defaults (deep-link race).
let resolveFlagsLoaded!: () => void;
export const flagsLoaded: Promise<void> = new Promise((r) => { resolveFlagsLoaded = r; });

const FLAG_NAME_MAP: Record<string, keyof typeof defaultFeatureFlags> = {
  'CREATE WORKFLOW': 'createWorkflow',
  'CHAT PANEL': 'chatPanel',
  'MONITORS': 'monitors',
  'ICP DASHBOARD': 'icpDashboard',
  'CAMPAIGN SCHEDULING': 'campaignScheduling',
  'CLIENT PORTAL': 'clientPortal',
  'EVENTS': 'portalEvents',
};

export const loadFeatureFlags = async (): Promise<void> => {
  try {
    const flags = await apiClient.getFeatureFlags();
    const mappedFlags: Record<string, boolean> = { ...defaultFeatureFlags };

    flags.forEach((flag: { name: string; enabled: boolean }) => {
      const frontendFlagName = FLAG_NAME_MAP[flag.name];
      if (frontendFlagName) {
        mappedFlags[frontendFlagName] = flag.enabled;
      }
    });

    runtimeFeatureFlags = mappedFlags;
  } catch (error) {
    console.error('Failed to load feature flags, using defaults:', error);
    runtimeFeatureFlags = { ...defaultFeatureFlags };
  } finally {
    resolveFlagsLoaded();
  }
};

export const isFeatureEnabled = (feature: keyof typeof defaultFeatureFlags): boolean => {
  return runtimeFeatureFlags[feature] ?? defaultFeatureFlags[feature];
};

export const getFeatureFlags = () => runtimeFeatureFlags;
